import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, firstValueFrom } from 'rxjs';

import { isBackendErrorEvent } from '../../../../backend/BackendErrorEvent';
import { RequestWarning, RequestWarningType } from '../../../utils/requests/RequestWarning';
import { ValidationService } from '../../../utils/valibot/validation.service';
import { GcdmCreateCustomerInput } from './model/create-customer-request.model';
import { GcdmCreateCustomerResponse, GcdmCreateCustomerResponseSchema } from './model/create-customer-response.model';
import { GcdmCustomerQueryOptions } from './model/request.model';
import {
  GcdmBusinessPartner,
  GcdmBusinessPartnerResponse,
  GcdmBusinessPartnerResponseSchema,
  GcdmSingleBusinessPartnerSchema,
} from './model/response.model';

// The error types returned from CDF's GCDM endpoints, with the prefix 'GCDM_'.
enum GcdmErrorType {
  GCDM_TOO_MANY_CUSTOMERS_FOUND = 'GCDM_TOO_MANY_CUSTOMERS_FOUND',
}

/**
 * For external APIs like this we decided to only soft-validate responses using Valibot schemas.
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmCustomerClient {
  private static readonly urlPrefix = `gcdm`;

  readonly http = inject(HttpClient);
  readonly logger = inject(NGXLogger);
  private readonly validationService = inject(ValidationService);

  public searchCustomer(options: GcdmCustomerQueryOptions): Promise<GcdmBusinessPartnerResponse> {
    let queryParams = new HttpParams();

    // Dynamically set query parameters if they exist
    Object.entries(options).forEach(([key, value]) => {
      if (value != undefined) {
        queryParams = queryParams.set(key, value as string);
      }
    });

    const url = `${GcdmCustomerClient.urlPrefix}/customers`;
    this.logger.info(`#CDFTF0262 Searching for customers ${url}`);
    return firstValueFrom(
      this.http.get(url, { params: queryParams }).pipe(
        this.validationService.pipeResponseSoftFail(GcdmBusinessPartnerResponseSchema),
        catchError((error: HttpErrorResponse) => {
          // Handle 404 with type GCDM_TOO_MANY_CUSTOMERS_FOUND as a request warning
          if (this.errorHasType(error.error, GcdmErrorType.GCDM_TOO_MANY_CUSTOMERS_FOUND)) {
            this.logger.info(
              '#CDFFF0258 The NSC Database found too many results, asking the user to refine the search.',
              error,
            );
            throw new RequestWarning(
              RequestWarningType.TOO_MANY_RESULTS,
              'The NSC Database found too many results when searching for customers.',
            );
          }

          this.logger.info('#CDFTF0263 Unable to search for customers in the NSC Database', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Creates a customer in the NSC Database (GCDM) and returns the assigned GCID.
   *
   * Unlike the ESO CRM detour it replaces, the GCID is available immediately - no polling
   * for a delayed assignment is needed (CUSTAPP-611).
   */
  public createCustomer(input: GcdmCreateCustomerInput): Promise<GcdmCreateCustomerResponse> {
    const url = `${GcdmCustomerClient.urlPrefix}/customers`;
    this.logger.info(`#CDFTF0265 Creating customer in the NSC Database ${url}`);
    return firstValueFrom(
      this.http.post(url, input).pipe(
        this.validationService.pipeResponseSoftFail(GcdmCreateCustomerResponseSchema),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('#CDFTF0266 Unable to create customer in the NSC Database', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Writes the merged customer back to the NSC Database (GCDM).
   *
   * The backend performs a read-modify-write: GCDM matches addresses and communications by
   * their GUID, so it fetches the stored business partner first (CUSTAPP-611, case B2).
   */
  public updateCustomer(gcid: string, input: GcdmCreateCustomerInput): Promise<GcdmCreateCustomerResponse> {
    const url = `${GcdmCustomerClient.urlPrefix}/customers/${gcid}`;
    this.logger.info(`#CDFTF0270 Updating customer in the NSC Database ${url}`);
    return firstValueFrom(
      this.http.put(url, input).pipe(
        this.validationService.pipeResponseSoftFail(GcdmCreateCustomerResponseSchema),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 409) {
            // GCDM locks the GCID while an update runs; a retry usually succeeds, so this must
            // not be presented to the user as a validation failure.
            this.logger.info('#CDFTF0271 The NSC Database customer is being updated concurrently.', error);
            throw new RequestWarning(
              RequestWarningType.CONCURRENT_UPDATE,
              'The NSC Database customer is currently being updated by someone else.',
            );
          }

          this.logger.error('#CDFTF0272 Unable to update customer in the NSC Database', error);
          throw error;
        }),
      ),
    );
  }

  private errorHasType(error: unknown, type: GcdmErrorType): boolean {
    return isBackendErrorEvent(error) && error.type === type;
  }

  /**
   * Reads a single business partner by GCID.
   *
   * Used by the profile page of a synced customer, whose master data lives in the NSC Database
   * and only reaches Retail CRM through an asynchronous mirror.
   */
  public getCustomerByGcid(gcid: string): Promise<GcdmBusinessPartner> {
    const url = `${GcdmCustomerClient.urlPrefix}/customers/${gcid}`;
    this.logger.info(`#CDFTF0284 Reading customer from the NSC Database ${url}`);
    return firstValueFrom(
      this.http.get(url).pipe(
        this.validationService.pipeResponseSoftFail(GcdmSingleBusinessPartnerSchema),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            // The account carries a GCID that the NSC Database does not know (merged away, or
            // belonging to another business context). Not an outage - the caller falls back.
            this.logger.info('#CDFTF0285 The NSC Database does not know this GCID.', error);
            throw new RequestWarning(
              RequestWarningType.NOT_FOUND,
              'The NSC Database does not know the GCID of this customer.',
            );
          }

          this.logger.error('#CDFTF0286 Unable to read the customer from the NSC Database', error);
          throw error;
        }),
      ),
    );
  }
}
